import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import {
  Container,
  Row,
  Col,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Spinner,
} from "reactstrap"
import BreadcrumbTitle from "components/BreadcrumbTitle"
import PageMetaTags from "components/PageMetaTags"
import { connect } from "react-redux"
import {
  displayDefaultReports,
  getDashboardSections,
  getUserReports,
  updateDashboardSections,
} from "store/actions"
import {
  closestCenter,
  DndContext,
  DragOverlay,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { arrayMove, SortableContext } from "@dnd-kit/sortable"
import SortableWidgets from "./SortableWidgets"
import WidgetItem from "./WidgetItem"
import { DASHBOARD } from "constants/index"
import { buildReport } from "./utils"

const Dashboard = ({
  getUserReports,
  getDashboardSections,
  dashboardSections,
  userId,
  vendorId,
  updateDashboardSections,
  displayDefaultReports,
  dashboardReportsAreLoading,
}) => {
  const [displayedSections, setDisplayedSections] = useState([])
  const [moreReports, setMoreReports] = useState([])
  const [activeId, setActiveId] = useState(null)

  useEffect(() => {
    getDashboardSections({})
    getUserReports({})
  }, [])

  useEffect(() => {
    if (!!dashboardSections) {
      if (dashboardSections?.length === 0) {
        displayDefaultReports({ userId, vendorId })
      }
      let preparedReports = []
      const displayedData = dashboardSections?.map(section => section?.section)
      DASHBOARD?.SECTIONS?.forEach(section => {
        if (!displayedData?.includes(section)) {
          preparedReports.push(buildReport(section))
        }
      })
      setMoreReports(preparedReports)
      setDisplayedSections(dashboardSections)
    }
  }, [dashboardSections])

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        delay: 0,
        tolerance: 0,
      },
    }),
    useSensor(TouchSensor),
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  )

  const handleDragStart = event => {
    setActiveId(event?.active?.id)
  }

  const handleDragEnd = event => {
    updateDashboardSections({
      sections: displayedSections?.map((section, index) => ({
        ...section,
        order: index + 1,
      })),
      user_id: userId,
      vendor_id: vendorId,
    })
    setActiveId(null)
  }

  const handleDragOver = event => {
    const { active, over } = event

    if (active.id !== over.id) {
      setDisplayedSections(items => {
        const oldIndex = items.findIndex(i => i.section === active.id)
        const newIndex = items.findIndex(i => i.section === over.id)

        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const handleRemoveWidget = name => {
    updateDashboardSections({
      sections: displayedSections
        ?.filter(x => x?.section !== name)
        ?.map((item, index) => ({ ...item, order: index + 1 })),
      user_id: userId,
      vendor_id: vendorId,
    })
  }

  return (
    <React.Fragment>
      <div className="page-content" style={{ minHeight: "500px" }}>
        <PageMetaTags title="Home" />
        <Container fluid>
          <Row className="mb-2">
            <Col>
              <BreadcrumbTitle title={"Dashboard"} />
            </Col>
            {dashboardReportsAreLoading ? (
              <></>
            ) : (
              <Col xs="auto">
                <UncontrolledDropdown>
                  <DropdownToggle color="primary">
                    Add Report{" "}
                    <i className="mdi mdi-chevron-down d-none d-xl-inline-block ms-1" />
                  </DropdownToggle>
                  <DropdownMenu className="dropdown-menu-end">
                    {moreReports?.length > 0 ? (
                      moreReports?.map((item, index) => (
                        <DropdownItem
                          key={index}
                          onClick={() => {
                            updateDashboardSections({
                              sections: [
                                ...dashboardSections,
                                {
                                  section: item?.section,
                                  order: dashboardSections?.length + 1,
                                },
                              ],
                              user_id: userId,
                              vendor_id: vendorId,
                            })
                          }}
                        >
                          {item?.label}
                        </DropdownItem>
                      ))
                    ) : (
                      <DropdownItem text>No reports found</DropdownItem>
                    )}
                  </DropdownMenu>
                </UncontrolledDropdown>
              </Col>
            )}
          </Row>
          {dashboardReportsAreLoading ? (
            <Row className="d-flex justify-content-center">
              <Col xs="auto">
                <Spinner />
              </Col>
            </Row>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
            >
              <SortableContext
                items={displayedSections?.map(w => w?.section)}
                strategy={() => {}}
              >
                <Row>
                  {displayedSections?.map((w, index) => (
                    <SortableWidgets
                      id={w?.section}
                      name={w?.section}
                      key={index}
                      handleRemoveWidget={handleRemoveWidget}
                    />
                  ))}
                </Row>
              </SortableContext>
              <DragOverlay>
                {activeId ? (
                  <WidgetItem
                    isDragging
                    name={
                      displayedSections?.find(w => w?.section === activeId)
                        ?.section
                    }
                  />
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
        </Container>
      </div>
    </React.Fragment>
  )
}

Dashboard.propTypes = {
  t: PropTypes.any,
}

const mapStateToProps = state => ({
  vendorId: state.Login.vendorId,
  userId: state.Login.authUser.id,
  dashboardSections: state.Analytics.dashboardSections,
  dashboardReportsAreLoading: state.Analytics.dashboardReportsAreLoading,
})

const mapDispatchToProps = dispatch => ({
  getUserReports: values => dispatch(getUserReports(values)),
  getDashboardSections: values => dispatch(getDashboardSections(values)),
  updateDashboardSections: values => dispatch(updateDashboardSections(values)),
  displayDefaultReports: values => dispatch(displayDefaultReports(values)),
})

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard)
